<template>
  <el-dialog v-model="visible" width="min(760px, 94vw)" class="research-preference-dialog"
    :show-close="false" :close-on-click-modal="false" append-to-body destroy-on-close>
    <template #header>
      <div class="preference-header">
        <div class="preference-heading-icon" v-html="preferenceIcon"></div>
        <div>
          <h2>我的研究偏好</h2>
        </div>
        <button type="button" class="dialog-close" aria-label="关闭" @click="visible = false">×</button>
      </div>
    </template>

    <div v-loading="loading" class="preference-body">
      <template v-if="profileSchema">
        <div v-if="answersLoadFailed" class="preference-alert">
          <span>历史偏好加载失败，已暂停保存以避免覆盖已有偏好。</span>
          <button type="button" class="preference-retry" @click="loadPreferences">重试加载</button>
        </div>
        <section v-if="needsPersonalInfo" class="preference-section personal-info-section">
          <div class="section-heading">
            <h3>基本信息</h3>
            <p>首次保存研究偏好前，请补充称呼和单位。</p>
          </div>
          <div class="personal-info-grid">
            <el-input v-model="personalInfo.nickname" placeholder="该如何称呼您" />
            <el-input v-model="personalInfo.organization" placeholder="您的大学、公司或组织" />
          </div>
        </section>

        <section v-for="(question, index) in profileSchema.questions" :key="question.id" class="preference-section">
          <div class="section-heading">
            <h3>{{ index + 1 }}. {{ question.label }}</h3>
            <p>{{ question.description }}</p>
          </div>
          <el-radio-group
            v-if="question.ui_type === 'single_select'"
            v-model="formAnswers[question.id].selected[0]"
            class="preference-options preference-radio-options"
          >
            <el-radio v-for="option in question.options" :key="option.id"
              :label="option.id" class="preference-option preference-radio-option">
              <span class="preference-radio-text">
                <span class="preference-radio-title">{{ option.label }}</span>
                <span v-if="option.description" class="preference-radio-desc">{{ option.description }}</span>
              </span>
            </el-radio>
          </el-radio-group>
          <template v-else>
            <el-checkbox-group v-model="formAnswers[question.id].selected" class="preference-options">
              <el-checkbox v-for="option in displayOptions(question.options)" :key="option.id"
                :label="option.id" class="preference-option">
                {{ option.label }}
              </el-checkbox>
              <!-- 原型：「其他」选中后才展开补充输入框；已填过内容的直接展开。 -->
              <button type="button" class="preference-other" :class="{ 'is-checked': customOpen[question.id] }"
                :aria-pressed="!!customOpen[question.id]" @click="toggleCustom(question.id)">其他</button>
            </el-checkbox-group>
            <el-input v-if="customOpen[question.id]" v-model="formAnswers[question.id].customText"
              :placeholder="question.custom_text_placeholder || '输入其他研究方向、问题、数据或方法'"
              class="preference-custom-input" />
          </template>
        </section>

        <div v-if="!profileSchema.questions?.length" class="preference-empty">暂无可填写的研究偏好项。</div>

      </template>
      <div v-else-if="!loading" class="preference-empty">
        研究偏好加载失败，请关闭后重试。
        <button type="button" class="preference-retry" @click="loadPreferences">重试加载</button>
      </div>
    </div>

    <template #footer>
      <div class="preference-footer">
        <button type="button" class="preference-reset" @click="clearPreferences">重置选择</button>
        <div class="preference-footer-actions">
          <el-button size="large" @click="visible = false">暂时跳过</el-button>
          <el-button type="primary" size="large" :loading="saving" :disabled="saveBlocked" @click="savePreferences">
            保存偏好
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import {
  getResearchProfile,
  getResearchProfileSchema,
  updateResearchProfile,
} from '@/api/profile'

const props = defineProps({ modelValue: { type: Boolean, default: false } })
const emit = defineEmits(['update:modelValue', 'saved'])

const visible = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

const preferenceIcon = '<svg viewBox="0 0 24 24" fill="none"><path d="M4 7h10M18 7h2M4 17h3M11 17h9M8 4v6M15 14v6M4 12h4M12 12h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="16" cy="7" r="2" stroke="currentColor" stroke-width="1.8"/><circle cx="9" cy="17" r="2" stroke="currentColor" stroke-width="1.8"/></svg>'

const loading = ref(false)
const saving = ref(false)
const profileSchema = ref(null)
const formAnswers = reactive({})
const personalInfo = reactive({ nickname: '', organization: '' })
const needsPersonalInfo = computed(() => !personalInfo.nickname.trim() || !personalInfo.organization.trim())
/** 历史答案（schema-with-answers）加载失败: 此时表单是空的, 保存会覆盖已有偏好, 必须拦截 */
const answersLoadFailed = ref(false)
/** 保存按钮禁用: 加载中(表单可能尚未回显历史答案)、问卷未加载成功、或历史答案加载失败 */
const saveBlocked = computed(() => loading.value || !profileSchema.value || answersLoadFailed.value)

const displayOptions = options => options?.filter(option => option.id !== 'other') || []
/** 「其他」补充输入框是否展开：有已填内容时默认展开，收起时清空，避免看不见的文字被保存。 */
const customOpen = reactive({})
const toggleCustom = id => {
  customOpen[id] = !customOpen[id]
  if (!customOpen[id] && formAnswers[id]) formAnswers[id].customText = ''
}

const normalizeSelected = selected => {
  if (!Array.isArray(selected)) return []
  return selected.map(item => typeof item === 'object' ? item?.id : item).filter(Boolean)
}

const resetForm = schema => {
  Object.keys(formAnswers).forEach(key => delete formAnswers[key])
  Object.keys(customOpen).forEach(key => delete customOpen[key])
  for (const question of schema?.questions || []) {
    formAnswers[question.id] = { selected: [], customText: '' }
  }
}

const applyAnswers = answers => {
  for (const question of profileSchema.value?.questions || []) {
    const answer = answers?.[question.id]
    if (!answer) continue
    formAnswers[question.id].selected = normalizeSelected(answer.selected)
    formAnswers[question.id].customText = answer.custom_text || answer.customText || ''
    customOpen[question.id] = !!formAnswers[question.id].customText.trim()
  }
}

const loadPreferences = async () => {
  loading.value = true
  profileSchema.value = null
  answersLoadFailed.value = false
  try {
    const [schemaRes, profileRes] = await Promise.all([
      getResearchProfileSchema(),
      getResearchProfile(),
    ])
    if (schemaRes.data?.code !== 200) throw new Error('研究偏好配置加载失败')

    profileSchema.value = schemaRes.data.data
    resetForm(profileSchema.value)

    const profileData = profileRes.data?.code === 200 ? profileRes.data.data?.profileData : null
    personalInfo.nickname = profileData?.personal_info?.nickname || ''
    personalInfo.organization = profileData?.personal_info?.organization || ''
    applyAnswers(profileData)

    try {
      const answersRes = await axios.get('/api/user/profile/research/schema-with-answers')
      if (answersRes.data?.code === 200) {
        applyAnswers(answersRes.data.data?.registration_answers)
      } else {
        answersLoadFailed.value = true
      }
    } catch (error) {
      answersLoadFailed.value = true
      console.warn('加载研究偏好历史答案失败，已禁止保存以免覆盖已有偏好:', error)
    }
  } catch (error) {
    console.error('加载研究偏好失败:', error)
    ElMessage.error(error.message || '加载研究偏好失败')
  } finally {
    loading.value = false
  }
}

const clearPreferences = () => {
  for (const answer of Object.values(formAnswers)) {
    answer.selected = []
    answer.customText = ''
  }
  Object.keys(customOpen).forEach(key => delete customOpen[key])
}

const savePreferences = async () => {
  if (loading.value) {
    ElMessage.warning('偏好加载中，请稍候')
    return
  }
  if (!profileSchema.value) {
    ElMessage.error('研究偏好配置未加载，请关闭后重试')
    return
  }
  if (answersLoadFailed.value) {
    ElMessage.error('历史偏好加载失败，为避免覆盖已有偏好，请关闭后重试')
    return
  }
  if (needsPersonalInfo.value) {
    ElMessage.warning('请先补充称呼和单位')
    return
  }

  saving.value = true
  try {
    const answers = {
      personal_info: {
        nickname: personalInfo.nickname.trim(),
        organization: personalInfo.organization.trim(),
      },
    }
    for (const question of profileSchema.value?.questions || []) {
      const answer = formAnswers[question.id]
      answers[question.id] = {
        selected: [...answer.selected],
        customText: answer.customText.trim() || null,
      }
    }
    await updateResearchProfile({ answers })
    ElMessage.success('研究偏好已保存。')
    emit('saved')
    visible.value = false
  } catch (error) {
    ElMessage.error(error.message || '保存研究偏好失败')
  } finally {
    saving.value = false
  }
}

watch(() => props.modelValue, value => {
  if (value) loadPreferences()
})
</script>

<style scoped>
/* 2026-10-18 按原型 .bu-preference* 统一字体与信息密度：760 宽、18 圆角、标题 20/500、题目 16/500、
   说明与选项 14px、胶囊 8×14 / 圆角 10、协作方式三列卡片、按钮 40 高 / 圆角 9，全局 Inter + 微软雅黑 430。 */
:global(.research-preference-dialog.el-dialog) {
  --pref-text: #17233d;
  --pref-muted: #69778f;
  --pref-line: #e3e9f4;
  --pref-border: #d8e1f2;
  --pref-blue: #124ee8;
  --pref-selected: #edf3ff;
  max-height: 92vh;
  margin-top: 4vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--pref-line);
  border-radius: 18px;
  box-shadow: 0 24px 68px rgba(20, 38, 75, .22);
  color: var(--pref-text);
  font-family: Inter, "Microsoft YaHei", sans-serif;
  font-weight: 430;
}

:global(.research-preference-dialog .el-dialog__header) { margin: 0; padding: 0; flex-shrink: 0; }
:global(.research-preference-dialog .el-dialog__body) { padding: 0; flex: 1; min-height: 0; display: flex; flex-direction: column; }
:global(.research-preference-dialog .el-dialog__footer) { padding: 0; flex-shrink: 0; }

.preference-header {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px 28px 20px;
  border-bottom: 1px solid var(--pref-line);
}

.preference-heading-icon {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: var(--pref-blue);
  background: var(--pref-selected);
  border-radius: 13px;
}

.preference-heading-icon :deep(svg) { width: 22px; height: 22px; }
.preference-header h2 { margin: 0; color: var(--pref-text); font-size: 20px; line-height: 28px; font-weight: 500; }
.preference-header p { margin: 4px 0 0; color: var(--pref-muted); font-size: 14px; line-height: 1.6; }

.dialog-close {
  margin-left: auto;
  align-self: flex-start;
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 8px;
  color: var(--pref-muted);
  background: transparent;
  font-size: 22px;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
}
.dialog-close:hover { background: #f8faff; color: var(--pref-text); }

.preference-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 26px 20px 30px 28px;
  font-size: 14px;
}

/* 原型：题与题之间细分隔线，末题不画。 */
.preference-section { margin-bottom: 26px; padding-bottom: 26px; border-bottom: 1px solid var(--pref-line); }
.preference-section:last-of-type { margin-bottom: 0; padding-bottom: 0; border-bottom: 0; }
.section-heading h3 { margin: 0; color: var(--pref-text); font-size: 16px; line-height: 24px; font-weight: 500; }
.section-heading p { margin: 8px 0 14px; color: var(--pref-muted); font-size: 14px; line-height: 1.6; }
.section-heading p:empty { display: none; }
.section-heading h3:last-child { margin-bottom: 14px; }

.preference-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.preference-option {
  height: auto !important;
  margin: 0 !important;
  display: inline-flex;
  align-items: stretch;
}

.preference-option :deep(.el-checkbox__input) { display: none; }
.preference-option :deep(.el-checkbox__label) {
  min-height: 38px;
  padding: 8px 14px;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  color: var(--pref-muted);
  background: #fff;
  border: 1px solid var(--pref-border);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 430;
  line-height: 20px;
  transition: color .2s, border-color .2s, background .2s;
}
.preference-option:hover :deep(.el-checkbox__label) { border-color: #b8c7e5; color: var(--pref-text); }

.preference-option.is-checked :deep(.el-checkbox__label) {
  color: var(--pref-blue);
  border-color: var(--pref-blue);
  background: var(--pref-selected);
}

.preference-other {
  min-height: 38px;
  padding: 8px 14px;
  border: 1px solid var(--pref-border);
  border-radius: 10px;
  background: #fff;
  color: var(--pref-muted);
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  transition: color .2s, border-color .2s, background .2s;
}
.preference-other:hover { border-color: #b8c7e5; color: var(--pref-text); }
.preference-other.is-checked { color: var(--pref-blue); border-color: var(--pref-blue); background: var(--pref-selected); }
.preference-custom-input { margin-top: 12px; }

/* 单选题（协作方式）：原型三列卡片。 */
.preference-radio-options { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; align-items: stretch; }
.preference-radio-option { display: flex; width: 100%; height: auto !important; margin: 0 !important; }
.preference-radio-option :deep(.el-radio__input) { display: none; }
.preference-radio-option :deep(.el-radio__label) {
  flex: 1;
  padding: 16px;
  display: flex;
  box-sizing: border-box;
  color: var(--pref-muted);
  background: #fff;
  border: 1px solid var(--pref-border);
  border-radius: 10px;
  font-size: 14px;
  transition: color .2s, border-color .2s, background .2s;
  white-space: normal;
}
.preference-radio-option:hover :deep(.el-radio__label) { border-color: #b8c7e5; }
.preference-radio-option.is-checked :deep(.el-radio__label) {
  color: var(--pref-blue);
  border-color: var(--pref-blue);
  background: var(--pref-selected);
}
.preference-radio-text { display: flex; flex-direction: column; gap: 9px; }
.preference-radio-title { font-size: 16px; line-height: 1.45; font-weight: 500; color: var(--pref-text); }
.preference-radio-desc { font-size: 14px; line-height: 1.55; color: var(--pref-muted); font-weight: 400; }
.preference-radio-option.is-checked .preference-radio-title,
.preference-radio-option.is-checked .preference-radio-desc { color: var(--pref-blue); }

.preference-custom-input :deep(.el-input__wrapper),
.personal-info-grid :deep(.el-input__wrapper) {
  min-height: 42px;
  padding: 0 13px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 0 0 1px var(--pref-border) inset;
}
.preference-custom-input :deep(.el-input__inner),
.personal-info-grid :deep(.el-input__inner) { font-size: 14px; color: var(--pref-text); }

.personal-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }


.preference-empty { padding: 56px 16px; color: var(--pref-muted); text-align: center; }

.preference-alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 20px;
  padding: 10px 14px;
  border: 1px solid #f5dab1;
  border-radius: 10px;
  background: #fdf6ec;
  color: #b88230;
  font-size: 14px;
}

.preference-retry {
  display: inline-block;
  margin-left: 12px;
  padding: 5px 14px;
  border: 1px solid var(--pref-border);
  border-radius: 9px;
  background: #fff;
  color: var(--pref-blue);
  font-size: 14px;
  cursor: pointer;
}

.preference-retry:hover { background: #f4f7ff; }

.preference-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 28px 18px;
  background: #fff;
  border-top: 1px solid var(--pref-line);
}

.preference-reset {
  padding: 7px 0;
  border: 0;
  background: transparent;
  color: var(--pref-muted);
  font-size: 14px;
  cursor: pointer;
  transition: color .2s;
}

.preference-reset:hover { color: var(--pref-blue); }

.preference-footer-actions {
  display: flex;
  gap: 12px;
}

.preference-footer :deep(.el-button) {
  height: 40px;
  min-width: 0;
  margin: 0;
  padding: 9px 16px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 430;
}
.preference-footer :deep(.el-button:not(.el-button--primary)) { border-color: var(--pref-border); color: var(--pref-muted); background: transparent; }
.preference-footer :deep(.el-button--primary) { background: #0840e6; border-color: #0840e6; color: #fff; }

@media (max-width: 700px) {
  .preference-header { align-items: flex-start; padding: 20px 18px 16px; }
  .preference-body { padding: 20px 18px 12px; }
  .personal-info-grid,
  .preference-radio-options { grid-template-columns: 1fr; }
  .preference-footer { padding: 14px 18px 16px; }
}
</style>
